// CSSStyleSheet patch strategies used by runtime style writer.
// It prefers rule-level diff updates over full text replacement.
import { buildCssRulePatchSpans, shouldUseRulePatch } from './patch-ranges'

// Detect support for `CSSStyleSheet.replaceSync` on current style sheet.
export function canUseReplaceSync(sheet: CSSStyleSheet | null): sheet is CSSStyleSheet & { replaceSync: (css: string) => void } {
  // Return true when replaceSync API exists.
  return Boolean(sheet && typeof (sheet as { replaceSync?: unknown }).replaceSync === 'function')
}

// Detect support for rule-level patch operations on current style sheet.
export function canPatchCssRules(
  sheet: CSSStyleSheet | null,
): sheet is CSSStyleSheet & { insertRule: (rule: string, index?: number) => number; deleteRule: (index: number) => void } {
  // Return true when sheet exposes insertRule/deleteRule/cssRules.
  return Boolean(
    sheet
    && typeof sheet.insertRule === 'function'
    && typeof sheet.deleteRule === 'function'
    && typeof sheet.cssRules?.length === 'number',
  )
}

// Apply top-level rule patch by updating only changed middle span.
export function applyCssRulePatch(sheet: CSSStyleSheet, previousRules: string[], nextRules: string[]): boolean {
  // Guard against stale sheet state that does not match previous snapshot.
  if (sheet.cssRules.length !== previousRules.length) {
    return false
  } else {
    // Continue patch span calculation.
  }

  const prefix = findCommonPrefixLength(previousRules, nextRules)
  const suffix = findCommonSuffixLength(previousRules, nextRules, prefix)
  // Skip patch when all rules are already equal.
  if (prefix === previousRules.length && prefix === nextRules.length) {
    return true
  } else {
    // Continue delete/insert patch operations.
  }

  const spans = buildCssRulePatchSpans(previousRules, nextRules, prefix, suffix)
  // Fall back when patch operation cost is higher than full replace.
  if (!shouldUseRulePatch(previousRules.length, nextRules.length, spans)) {
    return false
  } else {
    // Continue applying patch spans in reverse index order.
  }

  try {
    // Apply spans in reverse so earlier indexes stay stable.
    for (let spanIndex = spans.length - 1; spanIndex >= 0; spanIndex -= 1) {
      const span = spans[spanIndex]
      // Delete outdated rules from end to start to keep indexes valid.
      for (let index = span.deleteEndExclusive - 1; index >= span.deleteStart; index -= 1) {
        sheet.deleteRule(index)
      }

      let insertIndex = span.deleteStart
      // Insert changed target rules into this span gap.
      for (let index = span.insertStart; index < span.insertEndExclusive; index += 1) {
        sheet.insertRule(nextRules[index], insertIndex)
        insertIndex += 1
      }
    }
  } catch {
    return false
  }

  // Return true when patch applied successfully.
  return true
}

// Find longest shared prefix size between previous and next rule lists.
function findCommonPrefixLength(previous: string[], next: string[]): number {
  const compareLength = Math.min(previous.length, next.length)
  let index = 0
  // Advance while rules stay equal in the same order.
  while (index < compareLength && previous[index] === next[index]) {
    index += 1
  }

  // Return common prefix length.
  return index
}

// Find longest shared suffix size while respecting known shared prefix.
function findCommonSuffixLength(previous: string[], next: string[], prefix: number): number {
  let count = 0
  const previousRemaining = previous.length - prefix
  const nextRemaining = next.length - prefix
  const compareLength = Math.min(previousRemaining, nextRemaining)

  // Compare from end while suffix rules stay equal.
  while (count < compareLength) {
    const previousIndex = previous.length - 1 - count
    const nextIndex = next.length - 1 - count
    if (previous[previousIndex] !== next[nextIndex]) {
      break
    } else {
      // Continue suffix matching.
    }
    count += 1
  }

  // Return shared suffix length.
  return count
}
